import { collection, addDoc, doc, getDoc, updateDoc, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { db } from '../lib/firebase';
import { WebshopOrder, OrderItem, Customer, OrderStatus, PaymentStatus } from '../types';
import { getWebshopSettings } from '../config/webshopConfig';

// Firebase Functions import
const functions = getFunctions();

// Helper function to convert Firestore timestamp
const convertTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  return new Date(timestamp);
};

class OrderService {
  
  // Rendelés leadása
  async createOrder(
    customerData: Customer,
    orderItems: OrderItem[],
    shippingMethod: string,
    paymentMethod: string,
    customerNotes?: string,
    foxpostLocation?: any
  ): Promise<{ success: boolean; orderId?: string; orderNumber?: string; error?: string }> {
    try {
      const webshopSettings = getWebshopSettings();
      
      // Szervezet adatok lekérése a CRM-ből
      let organizationData: any = null;
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', webshopSettings.organizationId));
        if (orgDoc.exists()) {
          organizationData = orgDoc.data();
        }
      } catch (error) {
        console.warn('Szervezet adatok lekérése sikertelen:', error);
      }
      
      // Árak számítása - biztosítjuk hogy minden érték szám legyen
      const subtotal = orderItems.reduce((sum, item) => {
        const totalPrice = typeof item.totalPrice === 'number' && !isNaN(item.totalPrice) ? item.totalPrice : 0;
        return sum + totalPrice;
      }, 0);
      
      const taxAmount = orderItems.reduce((sum, item) => {
        const totalPrice = typeof item.totalPrice === 'number' && !isNaN(item.totalPrice) ? item.totalPrice : 0;
        const taxRate = typeof item.taxRate === 'number' && !isNaN(item.taxRate) ? item.taxRate : 27;
        return sum + (totalPrice * taxRate / (100 + taxRate));
      }, 0);
      
      // Szállítási költség kalkuláció
      const shippingCost = this.calculateShippingCost(subtotal, shippingMethod, webshopSettings);
      const safeShippingCost = typeof shippingCost === 'number' && !isNaN(shippingCost) ? shippingCost : 0;
      const totalAmount = subtotal + safeShippingCost;
      
      // Rendelési szám generálása
      const orderNumber = await this.generateOrderNumber(webshopSettings.webshopId);
      
      // Rendelés objektum létrehozása (CRM kompatibilis formátum)
      const newOrder: any = {
        // CRM alapadatok
        number: orderNumber,
        organizationId: webshopSettings.organizationId,
        customerId: customerData.isRegistered ? 'webshop-customer' : 'guest',
        
        // Státusz
        status: 'pending' as OrderStatus,
        orderDate: new Date().toISOString(),
        
        // Ügyfél adatok (CRM formátum)
        customerName: customerData.companyName || customerData.name || '',
        customerAddress: customerData.billingAddress || customerData.address || '',
        customerTaxNumber: customerData.taxNumber || '',
        customerEmail: customerData.email || '',
        customerPhone: customerData.phone || '',
        contactName: customerData.name || '',
        
        // Szervezet adatok (webshop) - EZ AZ ELADÓ (CRM-ből lekérve)
        organizationName: organizationData?.name || webshopSettings.organizationName || webshopSettings.webshopName || 'LaptopMarket',
        organizationAddress: organizationData?.address || (webshopSettings.address 
          ? `${webshopSettings.address.street}, ${webshopSettings.address.city} ${webshopSettings.address.postalCode}, ${webshopSettings.address.country}`
          : 'Budapest, Magyarország'),
        organizationTaxNumber: organizationData?.taxNumber || '12345678-1-23',
        organizationRegistrationNumber: organizationData?.registrationNumber || '01-09-123456',
        representativeName: organizationData?.representativeName || 'Üzletvezető',
        
        // Pénzügyi adatok
        subtotal: Math.round(subtotal * 100) / 100,
        taxAmount: Math.round(taxAmount * 100) / 100,
        totalAmount: Math.round(totalAmount * 100) / 100,
        currency: webshopSettings.currency || 'HUF',
        
        // Szállítási adatok
        deliveryAddress: customerData.address || '',
        deliveryInstructions: customerNotes || '',
        paymentTerms: paymentMethod === 'transfer' ? 'Előre utalás' : 'Utánvét',
        paymentMethod: paymentMethod,
        
        // Rendelés tételek
        items: orderItems,
        
        // Jegyzetek
        notes: `Webshop rendelés: ${webshopSettings.webshopName}\n${customerNotes || ''}${foxpostLocation ? `\nFoxpost automata: ${foxpostLocation.name} (${foxpostLocation.operator_id})` : ''}`,
        
        // Webshop specifikus mezők
        webshopId: webshopSettings.webshopId,
        webshopName: webshopSettings.webshopName,
        webshopUrl: webshopSettings.webshopUrl,
        warehouseId: webshopSettings.warehouseId || webshopSettings.allowedWarehouseIds?.[0] || '',
        shippingMethod,
        shippingCost: Math.round(safeShippingCost * 100) / 100,
        paymentStatus: 'PENDING' as PaymentStatus,
        
        // Ügyfél részletes adatok (webshop formátum)
        customer: {
          name: customerData.name || '',
          email: customerData.email || '',
          phone: customerData.phone || '',
          address: customerData.address || '',
          city: customerData.city || '',
          postalCode: customerData.postalCode || '',
          country: customerData.country || 'Magyarország',
          isRegistered: customerData.isRegistered || false,
          createdAt: new Date()
        },
        
        // Foxpost automata (ha van)
        ...(foxpostLocation && {
          foxpostLocation: {
            operator_id: foxpostLocation.operator_id || '',
            name: foxpostLocation.name || '',
            city: foxpostLocation.city || '',
            street: foxpostLocation.street || '',
            zip: foxpostLocation.zip || '',
            ...(foxpostLocation.geolat && { geolat: foxpostLocation.geolat }),
            ...(foxpostLocation.geolong && { geolong: foxpostLocation.geolong }),
            ...(foxpostLocation.findme && { findme: foxpostLocation.findme })
          }
        }),
        
        // Időbélyegek
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'webshop-system'
      };
      
      // Rendelés mentése Firestore-ba
      const orderRef = await addDoc(collection(db, 'orders'), newOrder);
      
      // Email küldés és készlet frissítés a backend-en történik automatikusan
      // Firestore triggerek vagy scheduled functions révén
      
      return {
        success: true,
        orderId: orderRef.id,
        orderNumber: orderNumber
      };
      
    } catch (error) {
      console.error('Rendelés létrehozási hiba:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ismeretlen hiba történt'
      };
    }
  }
  
  // Rendelési szám generálása
  private async generateOrderNumber(webshopId: string): Promise<string> {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    // Webshop prefix (WS1, WS2, WS3)
    const webshopPrefix = webshopId.toUpperCase().replace('SHOP', 'WS');
    
    // Mai napi rendelések száma
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    
    const todayOrdersQuery = query(
      collection(db, 'orders'),
      where('webshopId', '==', webshopId),
      where('orderDate', '>=', todayStart),
      where('orderDate', '<', todayEnd),
      orderBy('orderDate', 'desc'),
      limit(1)
    );
    
    const todayOrdersSnapshot = await getDocs(todayOrdersQuery);
    const todayOrderCount = todayOrdersSnapshot.size;
    
    // Formátum: WS1-20240720-001
    const orderNumber = `${webshopPrefix}-${year}${month}${day}-${String(todayOrderCount + 1).padStart(3, '0')}`;
    
    return orderNumber;
  }
  
  // Szállítási költség kalkuláció
  private calculateShippingCost(subtotal: number, shippingMethod: string, settings: any): number {
    // Biztonságos subtotal érték
    const safeSubtotal = typeof subtotal === 'number' && !isNaN(subtotal) ? subtotal : 0;
    
    // Ingyenes szállítás küszöb ellenőrzése
    if (settings.freeShippingThreshold && safeSubtotal >= settings.freeShippingThreshold) {
      return 0;
    }
    
    // Szállítási módok alapján
    switch (shippingMethod) {
      case 'standard':
        return 1500; // Standard szállítás
      case 'express':
        return 3000; // Expressz szállítás
      case 'pickup':
        return 0; // Személyes átvétel
      default:
        return 1500;
    }
  }
  
  // Email küldés Firebase Function-nel
  private async sendOrderConfirmationEmail(orderId: string, order: Omit<WebshopOrder, 'id'>): Promise<void> {
    try {
      const sendOrderEmail = httpsCallable(functions, 'sendOrderConfirmationEmail');
      
      // Érintett raktárak összegyűjtése
      const affectedWarehouses = new Map<string, {
        warehouseId: string;
        warehouseName: string;
        items: OrderItem[];
      }>();

      order.items.forEach(item => {
        if (item.warehouseId && item.warehouseName) {
          if (!affectedWarehouses.has(item.warehouseId)) {
            affectedWarehouses.set(item.warehouseId, {
              warehouseId: item.warehouseId,
              warehouseName: item.warehouseName,
              items: []
            });
          }
          affectedWarehouses.get(item.warehouseId)!.items.push(item);
        }
      });
      
      await sendOrderEmail({
        orderId,
        orderData: {
          ...order,
          // Email template paraméterei
          customerName: order.customer.name || 'Tisztelt Vásárló',
          customerEmail: order.customer.email,
          orderNumber: order.orderNumber,
          totalAmount: order.totalAmount,
          currency: order.currency,
          webshopName: order.webshopName,
          webshopUrl: order.webshopUrl,
          items: order.items,
          // Raktár információk
          affectedWarehouses: Array.from(affectedWarehouses.values())
        }
      });
      
    } catch (error) {
      console.error('Email küldési hiba:', error);
      throw error;
    }
  }
  
  // Készlet frissítés rendelés után
  private async updateInventoryAfterOrder(orderItems: OrderItem[], warehouseId: string): Promise<void> {
    try {
      const updateInventory = httpsCallable(functions, 'updateInventoryAfterOrder');
      
      await updateInventory({
        orderItems: orderItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          warehouseId: warehouseId
        }))
      });
      
    } catch (error) {
      console.error('Készlet frissítési hiba:', error);
      throw error;
    }
  }
  
  // Rendelés lekérése ID alapján
  async getOrderById(orderId: string): Promise<WebshopOrder | null> {
    try {
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      
      if (!orderDoc.exists()) {
        return null;
      }
      
      const orderData = orderDoc.data();
      
      return {
        ...orderData,
        id: orderDoc.id,
        orderDate: convertTimestamp(orderData.orderDate),
        createdAt: convertTimestamp(orderData.createdAt),
        updatedAt: convertTimestamp(orderData.updatedAt),
        expectedDeliveryDate: orderData.expectedDeliveryDate ? convertTimestamp(orderData.expectedDeliveryDate) : undefined,
        deliveredDate: orderData.deliveredDate ? convertTimestamp(orderData.deliveredDate) : undefined,
        customer: {
          ...orderData.customer,
          createdAt: orderData.customer.createdAt ? convertTimestamp(orderData.customer.createdAt) : undefined
        }
      } as WebshopOrder;
      
    } catch (error) {
      console.error('Rendelés lekérési hiba:', error);
      return null;
    }
  }
  
  // Ügyfél rendelései (email alapján)
  async getCustomerOrders(customerEmail: string): Promise<WebshopOrder[]> {
    try {
      const webshopSettings = getWebshopSettings();
      
      const ordersQuery = query(
        collection(db, 'orders'),
        where('customer.email', '==', customerEmail),
        where('webshopId', '==', webshopSettings.webshopId),
        orderBy('orderDate', 'desc'),
        limit(20)
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const orders: WebshopOrder[] = [];
      
      ordersSnapshot.forEach(doc => {
        const orderData = doc.data();
        orders.push({
          ...orderData,
          id: doc.id,
          orderDate: convertTimestamp(orderData.orderDate),
          createdAt: convertTimestamp(orderData.createdAt),
          updatedAt: convertTimestamp(orderData.updatedAt),
          expectedDeliveryDate: orderData.expectedDeliveryDate ? convertTimestamp(orderData.expectedDeliveryDate) : undefined,
          deliveredDate: orderData.deliveredDate ? convertTimestamp(orderData.deliveredDate) : undefined,
          customer: {
            ...orderData.customer,
            createdAt: orderData.customer.createdAt ? convertTimestamp(orderData.customer.createdAt) : undefined
          }
        } as WebshopOrder);
      });
      
      return orders;
      
    } catch (error) {
      console.error('Ügyfél rendelések lekérési hiba:', error);
      return [];
    }
  }
  
  // Rendelés státusz frissítése (admin funkció)
  async updateOrderStatus(orderId: string, newStatus: OrderStatus, trackingNumber?: string): Promise<boolean> {
    try {
      const updateData: any = {
        status: newStatus,
        updatedAt: new Date()
      };
      
      if (trackingNumber) {
        updateData.trackingNumber = trackingNumber;
      }
      
      if (newStatus === 'DELIVERED') {
        updateData.deliveredDate = new Date();
      }
      
      await updateDoc(doc(db, 'orders', orderId), updateData);
      
      return true;
      
    } catch (error) {
      console.error('Rendelés státusz frissítési hiba:', error);
      return false;
    }
  }
  
  // Fizetési státusz frissítése
  async updatePaymentStatus(orderId: string, newPaymentStatus: PaymentStatus): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        paymentStatus: newPaymentStatus,
        updatedAt: new Date()
      });
      
      return true;
      
    } catch (error) {
      console.error('Fizetési státusz frissítési hiba:', error);
      return false;
    }
  }
}

export const orderService = new OrderService(); 